import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  Boxes, Save, Loader2, AlertCircle, CheckCircle, Play, Clock, Calculator, ChevronDown, Calendar
} from 'lucide-react'
import { syncApi } from '@/lib/api'

interface StockMinConfig {
  calculation_weeks: number
  min_stock_rule_1: number
  min_stock_rule_2: number
  last_calculation: string | null
  last_calculation_stats: {
    products_updated: number
    products_skipped: number
    errors: number
    duration_seconds: number
  } | null
}

interface CalculationProgress {
  current: number
  total: number
  status: 'idle' | 'running' | 'completed' | 'error'
  message: string
  updated: number
  skipped: number
  errors: number
}

interface CalculationStatus {
  is_running: boolean
  progress: CalculationProgress
}

// Generate week options with month equivalences
const weekOptions = [
  { weeks: 4, label: '4 semanas (1 mes)' },
  { weeks: 8, label: '8 semanas (2 meses)' },
  { weeks: 12, label: '12 semanas (3 meses)' },
  { weeks: 16, label: '16 semanas (4 meses)' },
  { weeks: 20, label: '20 semanas (5 meses)' },
  { weeks: 24, label: '24 semanas (6 meses)' },
  { weeks: 28, label: '28 semanas (7 meses)' },
  { weeks: 32, label: '32 semanas (8 meses)' },
  { weeks: 36, label: '36 semanas (9 meses)' },
  { weeks: 40, label: '40 semanas (10 meses)' },
  { weeks: 44, label: '44 semanas (11 meses)' },
  { weeks: 52, label: '52 semanas (12 meses)' },
]

export default function StockMinimumPage() {
  const queryClient = useQueryClient()

  // Local state for form
  const [calculationWeeks, setCalculationWeeks] = useState<number>(52)
  const [minStockRule1, setMinStockRule1] = useState<number>(1)
  const [minStockRule2, setMinStockRule2] = useState<number>(2)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [selectedFamily, setSelectedFamily] = useState<string>('')  // '' means all families
  const [pollingEnabled, setPollingEnabled] = useState(false)

  // Fetch current config
  const configQuery = useQuery({
    queryKey: ['stock-min-config'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/stock-min-config')
      return res.data as StockMinConfig
    }
  })

  // Fetch calculation status (with polling)
  const statusQuery = useQuery({
    queryKey: ['stock-min-calculation-status'],
    queryFn: async () => {
      const res = await axios.get('/api/stock-minimum/calculate/status')
      return res.data as CalculationStatus
    },
    refetchInterval: pollingEnabled ? 1000 : false
  })

  // Start/stop polling based on status
  useEffect(() => {
    if (statusQuery.data?.is_running) {
      setPollingEnabled(true)
    } else if (statusQuery.data?.progress.status === 'completed' || statusQuery.data?.progress.status === 'error') {
      setPollingEnabled(false)
      // Refresh config to get latest stats
      queryClient.invalidateQueries({ queryKey: ['stock-min-config'] })
    }
  }, [statusQuery.data, queryClient])

  // Fetch next scheduled run
  const nextRunQuery = useQuery({
    queryKey: ['stock-min-next-run'],
    queryFn: async () => {
      const res = await axios.get('/api/stock-minimum/next-run')
      return res.data as { next_run: string | null; debug?: { scheduler_exists: boolean; scheduler_running: boolean; all_jobs: string[] } }
    }
  })

  // Fetch families
  const familiesQuery = useQuery({
    queryKey: ['families-for-stock'],
    queryFn: () => syncApi.getFamilies()
  })

  // Initialize form from config
  useEffect(() => {
    if (configQuery.data) {
      setCalculationWeeks(configQuery.data.calculation_weeks)
      setMinStockRule1(configQuery.data.min_stock_rule_1)
      setMinStockRule2(configQuery.data.min_stock_rule_2)
    }
  }, [configQuery.data])

  // Save config mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (config: { calculation_weeks: number, min_stock_rule_1: number, min_stock_rule_2: number }) => {
      await axios.post('/api/family-config/stock-min-config', config)
    },
    onMutate: () => {
      setSaveStatus('saving')
    },
    onSuccess: () => {
      setSaveStatus('saved')
      queryClient.invalidateQueries({ queryKey: ['stock-min-config'] })
      setTimeout(() => setSaveStatus('idle'), 2000)
    },
    onError: () => {
      setSaveStatus('error')
    }
  })

  // Calculate stock minimum mutation
  const calculateMutation = useMutation({
    mutationFn: async (familyCode?: string) => {
      const res = await axios.post('/api/stock-minimum/calculate', { family_code: familyCode || null })
      return res.data
    },
    onSuccess: () => {
      setPollingEnabled(true)
      statusQuery.refetch()
    }
  })

  const handleSave = () => {
    saveConfigMutation.mutate({
      calculation_weeks: calculationWeeks,
      min_stock_rule_1: minStockRule1,
      min_stock_rule_2: minStockRule2
    })
  }

  const handleCalculate = () => {
    const familyName = selectedFamily
      ? familiesQuery.data?.families?.find((f) => f.code === selectedFamily)?.name || selectedFamily
      : 'todos los productos'

    if (confirm(`¿Iniciar el calculo de stock minimo para ${selectedFamily ? `la familia "${familyName}"` : 'todos los productos'}? Esta operacion puede tardar varios minutos.`)) {
      calculateMutation.mutate(selectedFamily || undefined)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca'
    const date = new Date(dateStr)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (configQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
          <p className="text-slate-500 font-medium">Cargando configuracion...</p>
        </div>
      </div>
    )
  }

  const config = configQuery.data
  const isRunning = statusQuery.data?.is_running || calculateMutation.isPending
  const progress = statusQuery.data?.progress

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
          <Boxes className="w-8 h-8 text-orange-600 dark:text-orange-400" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Stock Minimo
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Configuracion del calculo automatico de stock minimo
          </p>
        </div>
      </div>

      {/* Last Calculation Info */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-5 h-5 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ultimo Calculo</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">Fecha y hora</p>
            <p className="text-lg font-medium text-slate-900 dark:text-white">
              {formatDate(config?.last_calculation || null)}
            </p>
          </div>
          {config?.last_calculation_stats && (
            <>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Productos actualizados</p>
                <p className="text-lg font-medium text-success-600 dark:text-success-400">
                  {config.last_calculation_stats.products_updated}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Productos sin cambios</p>
                <p className="text-lg font-medium text-slate-600 dark:text-slate-400">
                  {config.last_calculation_stats.products_skipped}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Duracion</p>
                <p className="text-lg font-medium text-slate-900 dark:text-white">
                  {config.last_calculation_stats.duration_seconds}s
                </p>
              </div>
            </>
          )}
          <div className="md:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Calendar className="w-4 h-4" />
              <span>Proximo calculo programado</span>
            </div>
            <p className="text-lg font-medium text-primary-600 dark:text-primary-400">
              {nextRunQuery.isLoading ? 'Cargando...' :
               nextRunQuery.data?.next_run
                ? formatDate(nextRunQuery.data.next_run)
                : 'No programado'}
            </p>
          </div>
        </div>

        {/* Progress Display */}
        {progress && progress.status !== 'idle' && (
          <div className="mt-6 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {progress.status === 'running' ? 'Procesando...' :
                 progress.status === 'completed' ? 'Completado' :
                 progress.status === 'error' ? 'Error' : ''}
              </span>
              {progress.status === 'running' && (
                <span className="text-sm text-slate-500">
                  {progress.current} / {progress.total}
                </span>
              )}
            </div>

            {progress.total > 0 && (
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    progress.status === 'error' ? 'bg-danger-500' :
                    progress.status === 'completed' ? 'bg-success-500' : 'bg-orange-500'
                  }`}
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-400">
              {progress.message}
            </p>

            {(progress.status === 'completed' || progress.status === 'error') && (
              <div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Actualizados</p>
                  <p className="text-lg font-semibold text-success-600 dark:text-success-400">
                    {progress.updated}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Sin cambios</p>
                  <p className="text-lg font-semibold text-slate-600 dark:text-slate-400">
                    {progress.skipped}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Errores</p>
                  <p className="text-lg font-semibold text-danger-600 dark:text-danger-400">
                    {progress.errors}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Family selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Familia:
            </label>
            <div className="relative">
              <select
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                disabled={isRunning}
                className="appearance-none pl-4 pr-10 py-2.5 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 disabled:opacity-50 min-w-[200px]"
              >
                <option value="">Todas las familias</option>
                {familiesQuery.data?.families?.map((family) => (
                  <option key={family.code} value={family.code}>
                    {family.code} - {family.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          <button
            onClick={handleCalculate}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Calcular Ahora
              </>
            )}
          </button>

          {!isRunning && progress?.status === 'completed' && (
            <div className="flex items-center gap-2 text-success-600 dark:text-success-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Ultimo calculo completado</span>
            </div>
          )}

          {!isRunning && progress?.status === 'error' && (
            <div className="flex items-center gap-2 text-danger-600 dark:text-danger-400">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Error en el ultimo calculo</span>
            </div>
          )}
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calculator className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Configuracion del Calculo</h2>
        </div>

        <div className="space-y-6">
          {/* Calculation Period */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Periodo de calculo de ventas
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Numero de semanas de historico de ventas a utilizar para calcular la media semanal de ventas por producto.
            </p>
            <select
              value={calculationWeeks}
              onChange={(e) => setCalculationWeeks(parseInt(e.target.value))}
              className="w-full max-w-md px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500"
            >
              {weekOptions.map(opt => (
                <option key={opt.weeks} value={opt.weeks}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Minimum Rules */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-md font-medium text-slate-900 dark:text-white mb-4">
              Reglas de Stock Minimo
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Estas reglas garantizan un stock minimo incluso para productos con pocas ventas.
            </p>

            <div className="space-y-4">
              {/* Rule 1 */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                <div className="flex-1">
                  <p className="font-medium text-slate-900 dark:text-white">
                    Si se ha vendido al menos 1 unidad en el periodo
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    El stock minimo sera al menos:
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={minStockRule1}
                    onChange={(e) => setMinStockRule1(parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 text-center border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                  <span className="text-slate-600 dark:text-slate-400">uds</span>
                </div>
              </div>

              {/* Rule 2 */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                <div className="flex-1">
                  <p className="font-medium text-slate-900 dark:text-white">
                    Si se han vendido al menos 2 unidades en el periodo
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    El stock minimo sera al menos:
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={minStockRule2}
                    onChange={(e) => setMinStockRule2(parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 text-center border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                  <span className="text-slate-600 dark:text-slate-400">uds</span>
                </div>
              </div>
            </div>
          </div>

          {/* Formula explanation */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Formula de calculo</h4>
              <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <p><strong>Media semanal</strong> = Ventas totales del periodo / {calculationWeeks} semanas</p>
                <p><strong>Stock minimo</strong> = Media semanal x Semanas de stock de la familia (o excepcion del producto)</p>
                <p className="mt-2 text-xs opacity-80">
                  Se aplican las reglas de minimos configuradas arriba cuando corresponda.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <span className="text-primary-500 text-sm flex items-center gap-1">
                <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-success-500 text-sm flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Guardado
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-danger-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> Error al guardar
              </span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saveConfigMutation.isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            Guardar Configuracion
          </button>
        </div>
      </div>
    </div>
  )
}
